import { useState } from "react";

import { GridRowSelectionModel } from "@mui/x-data-grid";
import { emptyRowSelection, useAlertContext } from "@shared";
import { ALERT_DURATION } from "@shared";

import { ProceedingParticipantSearchResult } from "@/api";
import { useTranslation } from "@/translations";

export const MAX_MERGE_SELECTION = 2;

export function useMergeMode(participants: ProceedingParticipantSearchResult[]) {
  const { t } = useTranslation();
  const { showWarning } = useAlertContext();
  const [isMergeMode, setIsMergeMode] = useState(false);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(emptyRowSelection);

  const selectedParticipants = participants.filter((p) =>
    (selectionModel.ids as Set<string>).has(p.proceedingParticipantId)
  );
  const uniqueTypes = new Set(selectedParticipants.map((p) => p.type));
  const hasMixedTypes = uniqueTypes.size > 1;
  const isValidSelection = selectedParticipants.length === MAX_MERGE_SELECTION && uniqueTypes.size === 1;

  const onSelectionChange = (model: GridRowSelectionModel) => {
    const nextIds = model.ids as Set<string>;
    const currentIds = selectionModel.ids as Set<string>;
    const isAddingEntry = nextIds.size > currentIds.size;
    if (isAddingEntry && nextIds.size > MAX_MERGE_SELECTION) {
      showWarning(
        t("proceedingParticipants.merge.maxSelectionWarning", { max: MAX_MERGE_SELECTION }),
        undefined,
        ALERT_DURATION
      );
      return;
    }
    setSelectionModel(model);
  };

  const enterMergeMode = () => setIsMergeMode(true);
  const exitMergeMode = () => {
    setIsMergeMode(false);
    setSelectionModel(emptyRowSelection());
  };
  const clearSelection = () => setSelectionModel(emptyRowSelection());

  return {
    isMergeMode,
    selectionModel,
    onSelectionChange,
    selectedParticipants,
    hasMixedTypes,
    isValidSelection,
    enterMergeMode,
    exitMergeMode,
    clearSelection,
  };
}
